import { throwForFailedResponse } from "@/lib/errors/error-handler";
import { CustomerResponse, BankAccount, AddressForm } from "@/types/customer";
import { update } from "lodash";

type UpdatePinPayload = {
  pin: string;
  password: string;
};

async function getJson<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options?.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));

  if (!res.ok || data?.success === false) {
    throwForFailedResponse(res, data, "Request failed");
  }
  return (data?.data ?? data) as T;
}

export const customerService = {
  async getCustomer(): Promise<CustomerResponse> {
    return getJson<CustomerResponse>("/api/customer");
  },

  async getBankAccounts(customerId: string | number): Promise<BankAccount[]> {
    return getJson<BankAccount[]>(`/api/customer/bank-accounts/${customerId}`);
  },

  async updatePin(payload: UpdatePinPayload) {
    return getJson<{ message?: string }>("/api/auth/update-pin", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async updateResidenceCountry(payload: { residence_country_id: string }) {
    return getJson<{ message?: string }>("/api/auth/update-residence-country", {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },

  async verifyPin(payload: { pin: string }) {
    return getJson<{ message?: string }>("/api/auth/verify-pin", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async freeze() {
    return getJson<{ message?: string }>("/api/customer/freeze", {
      method: "POST",
      // body: JSON.stringify(payload),
    });
  },

  async updateAddress(payload: AddressForm) {
    const res = await fetch("/api/customer/address", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throwForFailedResponse(res, data, "Failed to complete request");
    }
    return data;
  },

  async updateVellId(payload: { vell_id: string }) {
    const res = await fetch("/api/customer/vell-id", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throwForFailedResponse(res, data, "Failed to complete request");
    }
    return data;
  },

  async withdrawFunds(payload: {
    customer_id: number;
    wallet_id: string;
    account_id: string;
    amount_cents: number;
    reference_id: string;
  }) {
    return getJson<{ message?: string; data?: unknown }>("/api/payout/withdraw", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async updateProfilePicture(formData: FormData) {
    const res = await fetch("/api/customer/profile/upload-profile-picture", {
      method: "POST",
      body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throwForFailedResponse(res, data, "Failed to update profile picture");
    }
    return data;
  },

  async provider(body: string) {
    const res = await fetch(`/api/customer/provider`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ provider: body }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throwForFailedResponse(res, data, "Failed to complete request");
    }
    return data;
  },
};
